/*
 * © Copyright 2014 – 2023 Open Text or one of its affiliates.
 *
 * The only warranties for products and services of Open Text and its affiliates and licensors ("Open Text") are as
 * may be set forth in the express warranty statements accompanying such products and services. Nothing herein should be
 * constituting an additional warranty. Open Text shall not be liable for technical or editorial errors or omissions
 * contained herein.
 *
 * The information contained herein is subject to change without notice.
 */

import * as types from '../actions/action-types';
import {produce} from 'immer';
import {generateEmptyLoginFormData} from '../components/login/LoginMethodElements';

export const AUTHENTICATION_STATES = {
    LOGGED_IN: 'LOGGED_IN',
    METHOD_AUTHENTICATION: 'METHOD_AUTHENTICATION',
    UNINITIALIZED: 'UNINITIALIZED',
    OSP_AUTHENTICATION: 'OSP_AUTHENTICATION',
    FETCH_MULTITENANCY_STATUS: 'FETCH_MULTITENANCY_STATUS',
    USERNAME_ENTRY: 'USERNAME_ENTRY',
};

let defaultState = {
    chains: null,
    chainIndex: 0,
    loginFormData: {},
    loginSessionId: null,
    methodIndex: 0,
    status: AUTHENTICATION_STATES.FETCH_MULTITENANCY_STATUS,
    userId: '',
    username: '',    // The API uses user_name and username; username is more common, so we use this convention here
    tenantName: 'TOP'   //TOP is the default tenant name used by AA, default to this if no tenant is entered
};
// THIS IS ONLY FOR DEVELOPMENT, IN PROD THIS CODE BLOCK WILL BE SKIPPED.
if (process.env.NODE_ENV === 'development') {
    defaultState = {...defaultState, status: AUTHENTICATION_STATES.USERNAME_ENTRY};
}
const initialState = {...defaultState, status: AUTHENTICATION_STATES.UNINITIALIZED};
const authenticationReducer = (state = initialState, action) => produce(state, draft => {
    let methods;
    switch (action.type) {
        case types.AUTHENTICATE_USER_SUCCESS:
            methods = draft.chains[draft.chainIndex].methods;
            if (draft.methodIndex + 1 < methods.length) {
                draft.loginFormData = generateEmptyLoginFormData(methods[draft.methodIndex + 1]);
                draft.methodIndex++;
            }
            else {
                draft.chainIndex = 0;
                draft.loginFormData = {};
                draft.methodIndex = 0;
                draft.loginSessionId = action.result.loginSessionId;
                draft.status = AUTHENTICATION_STATES.LOGGED_IN;
                draft.username = action.result.userName;
                draft.userEmail = action.result.userEmail;
                draft.userId = action.result.userId;
                draft.userMobilePhone = action.result.userMobilePhone;
            }
            return;
        case types.CLEAR_USER_INFO:
            return defaultState;
        case types.LOAD_LOGIN_SESSION_INFO:
            draft.loginSessionId = action.loginSessionId;
            draft.status = AUTHENTICATION_STATES.LOGGED_IN;
            draft.tenantName = action.result.tenantName;
            draft.username = action.result.userName;
            draft.userEmail = action.result.userEmail;
            draft.userId = action.result.userId;
            draft.userMobilePhone = action.result.userMobilePhone;
            return;
        case types.LOGIN_CHAINS_SUCCESS:
            draft.chains = action.chains;
            methods = action.chains[draft.chainIndex].methods;
            draft.chainIndex = 0;
            draft.loginFormData = generateEmptyLoginFormData(methods[0]);
            draft.methodIndex = 0;
            draft.status = AUTHENTICATION_STATES.METHOD_AUTHENTICATION;
            return;
        case types.SET_MULTI_TENANCY_STATUS:
            draft.multitenancyEnabled = action.tenancyMode;
            draft.status = AUTHENTICATION_STATES.USERNAME_ENTRY;
            return;
        case types.UPDATE_LOGIN_FORM_DATA:
            draft.loginFormData[action.key] = action.value;
            return;
        case types.UPDATE_LOGIN_CHAIN:
            draft.chainIndex = action.index;
            return;
        case types.UPDATE_USERNAME:
            draft.username = action.username;
            return;
        default:
            return;
    }
});

export default authenticationReducer;
